/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  Checkbox,
  ControlledDateTimePicker,
  ControlledInput,
  ControlledSelect,
  ControlledTextarea,
  FormSelect,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import {
  makeIdempotencyKey,
  useNotifications,
} from "../../store/useNotifications";
import { ReactComponent as DroppieIcon } from "../../assets/icons/notifications/notification-icon.svg";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useUsers } from "../../store/useUsers";
import { useArticles } from "../../store/useArticles";
import { useAlert } from "../../store/useAlert";
import { useDropLocations } from "../../store/useDropLocations";

type Inputs = {
  title: string;
  body: string;
  type: "NOW" | "SCHEDULED";
  scheduledDate?: Date;
};

function AddNotificationScreen() {
  /** Hooks */
  const { getDropLocations, dropLocations } = useDropLocations();
  const { getArticles, articles } = useArticles();
  const { getUsersCount, usersCount } = useUsers();
  const {
    clearCreatedNotification,
    createdNotification,
    sendInstantNotificationToAll,
    sendScheduledNotificationToAll,
  } = useNotifications();
  // One key for this compose session. The screen unmounts on success
  // (navigate(-1) below), so every new message gets a fresh one, while a
  // double click on Send reuses it and the backend rejects the second
  // submission instead of queueing the broadcast twice.
  const idempotencyKey = useRef(makeIdempotencyKey());
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [notificationType, setNotificationType] = useState<string | null>(null);
  const [articleId, setArticleId] = useState<string | null>(null);
  const [
    showUsersWithoutDropsInLast5Days,
    setShowUsersWithoutDropsInLast5Days,
  ] = useState(false);
  const [allAdmins, setAllAdmins] = useState<boolean>(false);
  const [superAdminsOnly, setSuperAdminsOnly] = useState<boolean>(false);
  const [usersWithoutPostcode, setUsersWithoutPostcode] =
    useState<boolean>(false);
  const [droppedMaterials, setDroppedMaterials] = useState<string[]>([]);
  const [showDroppedMaterials, setShowDroppedMaterials] =
    useState<boolean>(false);
  const [nonDroppedMaterials, setNonDroppedMaterials] = useState<string[]>([]);
  const [showNonDroppedMaterials, setShowNonDroppedMaterials] =
    useState<boolean>(false);
  const [
    showUsersFromSpecificLocations,
    setShowUsersFromSpecificLocations,
  ] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  /** Effects */
  useEffect(() => {
    getUsersCount(
      showDroppedMaterials ? droppedMaterials : [],
      showNonDroppedMaterials ? nonDroppedMaterials : [],
      showUsersWithoutDropsInLast5Days,
      allAdmins,
      usersWithoutPostcode,
      selectedLocations,
      superAdminsOnly
    );
  }, [
    droppedMaterials,
    showUsersWithoutDropsInLast5Days,
    allAdmins,
    superAdminsOnly,
    nonDroppedMaterials,
    showDroppedMaterials,
    showNonDroppedMaterials,
    usersWithoutPostcode,
    selectedLocations,
  ]);

  useEffect(() => {
    getAllMarketPrices();
    getDropLocations();
    getArticles(1, 1000);
  }, []);
  useEffect(() => {
    if (createdNotification) {
      useAlert.getState().setAlert({
        isOpen: true,
        message:
          "Notification queued. It is sent in the background over the next few minutes; follow the progress in the list.",
        severity: "success",
      });
      clearCreatedNotification();
      navigate(-1);
    }
  }, [createdNotification]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    if (notificationType === "visit_article" && !articleId) {
      setSubmitted(true);
      useAlert.getState().setAlert({
        isOpen: true,
        message: "Please select an article to send notification",
        severity: "error",
      });
      return;
    }
    const confirmSend = window.confirm(
      "Are you sure you want to send this Notification?"
    );
    if (confirmSend) {
      const notification = { title: data?.title, body: data?.body };
      const notificationData: any = {};
      if (notificationType === "fill_postalcode") {
        notificationData.type = notificationType;
      }
      if (notificationType === "visit_article" && articleId) {
        notificationData.type = notificationType;
        notificationData.id = articleId;
      }
      if (data.type === "NOW") {
        sendInstantNotificationToAll(
          notification,
          notificationData,
          showDroppedMaterials ? droppedMaterials : [],
          showNonDroppedMaterials ? nonDroppedMaterials : [],
          showUsersWithoutDropsInLast5Days,
          allAdmins,
          usersWithoutPostcode,
          selectedLocations,
          superAdminsOnly,
          idempotencyKey.current
        );
      } else {
        if (data?.scheduledDate) {
          sendScheduledNotificationToAll(
            notification,
            notificationData,
            data.scheduledDate,
            showDroppedMaterials ? droppedMaterials : [],
            showNonDroppedMaterials ? nonDroppedMaterials : [],
            showUsersWithoutDropsInLast5Days,
            allAdmins,
            usersWithoutPostcode,
            selectedLocations,
            superAdminsOnly,
            idempotencyKey.current
          );
        }
      }
    }
  };

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Send a Notification
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Notification details
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              name="title"
              control={control}
              error={Boolean(errors?.title)}
              label="Title"
              placeholder="Notification title"
              rules={{ required: true }}
            />
            <ControlledTextarea
              name="body"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.body)}
              label="Body"
              placeholder="Type notification body here..."
              inputClassName="min-h-[180px]"
            />
            <FormSelect
              options={[
                { label: "Default", value: "default" },
                { label: "Visit Article", value: "visit_article" },
                { label: "Fill Postalcode", value: "fill_postalcode" },
              ]}
              placeholder="Notification type here..."
              value={notificationType as string}
              onChange={(e) => {
                setNotificationType(e.target.value);
              }}
            />
            {notificationType === "visit_article" ? (
              <FormSelect
                error={submitted && !articleId}
                placeholder="Choose article"
                // @ts-ignore
                options={articles?.map((article) => ({
                  label: article.title,
                  value: article._id,
                }))}
                value={articleId as string}
                onChange={(e) => {
                  setArticleId(e.target.value);
                }}
              />
            ) : null}
            <Checkbox
              value={usersWithoutPostcode}
              onChange={(e) => {
                if (e.target.checked) {
                  setUsersWithoutPostcode(true);
                  setAllAdmins(false);
                  setSuperAdminsOnly(false);
                  setShowUsersWithoutDropsInLast5Days(false);
                  setShowDroppedMaterials(false);
                  setShowNonDroppedMaterials(false);
                  setShowUsersFromSpecificLocations(false);
                  setDroppedMaterials([]);
                  setNonDroppedMaterials([]);
                  setSelectedLocations([]);
                } else {
                  setUsersWithoutPostcode(false);
                }
              }}
              label="Users without postcode"
            />
            <Checkbox
              value={allAdmins}
              onChange={(e) => {
                if (e.target.checked) {
                  setAllAdmins(true);
                  setSuperAdminsOnly(false);
                  setUsersWithoutPostcode(false);
                  setShowUsersWithoutDropsInLast5Days(false);
                  setShowDroppedMaterials(false);
                  setShowNonDroppedMaterials(false);
                  setShowUsersFromSpecificLocations(false);
                  setDroppedMaterials([]);
                  setNonDroppedMaterials([]);
                  setSelectedLocations([]);
                } else {
                  setAllAdmins(false);
                }
              }}
              label="All admins"
            />
            <Checkbox
              value={superAdminsOnly}
              onChange={(e) => {
                if (e.target.checked) {
                  setSuperAdminsOnly(true);
                  setAllAdmins(false);
                  setUsersWithoutPostcode(false);
                  setShowUsersWithoutDropsInLast5Days(false);
                  setShowDroppedMaterials(false);
                  setShowNonDroppedMaterials(false);
                  setShowUsersFromSpecificLocations(false);
                  setDroppedMaterials([]);
                  setNonDroppedMaterials([]);
                  setSelectedLocations([]);
                } else {
                  setSuperAdminsOnly(false);
                }
              }}
              label="Super admins only"
            />
            <Checkbox
              value={showUsersWithoutDropsInLast5Days}
              onChange={(e) => {
                if (e.target.checked) {
                  setShowUsersWithoutDropsInLast5Days(true);
                  setUsersWithoutPostcode(false);
                  setAllAdmins(false);
                  setSuperAdminsOnly(false);
                  setShowDroppedMaterials(false);
                  setShowNonDroppedMaterials(false);
                  setShowUsersFromSpecificLocations(false);
                  setDroppedMaterials([]);
                  setNonDroppedMaterials([]);
                  setSelectedLocations([]);
                } else {
                  setShowUsersWithoutDropsInLast5Days(false);
                }
              }}
              label="Users that have not dropped in the last 5 days"
            />
            <Checkbox
              value={showDroppedMaterials}
              onChange={(e) => {
                if (e.target.checked) {
                  setShowDroppedMaterials(true);
                  setUsersWithoutPostcode(false);
                  setAllAdmins(false);
                  setSuperAdminsOnly(false);
                  setShowUsersWithoutDropsInLast5Days(false);
                  setShowNonDroppedMaterials(false);
                  setShowUsersFromSpecificLocations(false);
                  setNonDroppedMaterials([]);
                  setSelectedLocations([]);
                } else {
                  setShowDroppedMaterials(false);
                  setDroppedMaterials([]);
                }
              }}
              label="Users that have dropped the following materials:"
            />
            {showDroppedMaterials ? (
              <div className="w-full grid grid-cols-2 p-2 border border-gray-border rounded-xl">
                {marketPrices?.map((price) => {
                  const isChecked = droppedMaterials.includes(
                    price._id as string
                  );
                  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setDroppedMaterials([
                        ...droppedMaterials,
                        price._id as string,
                      ]);
                    } else {
                      setDroppedMaterials(
                        droppedMaterials.filter((item) => item !== price._id)
                      );
                    }
                  };

                  return (
                    <Checkbox
                      key={price._id}
                      value={isChecked}
                      label={price.name}
                      onChange={onChange}
                    />
                  );
                })}
              </div>
            ) : null}
            <Checkbox
              value={showNonDroppedMaterials}
              onChange={(e) => {
                if (e.target.checked) {
                  setShowNonDroppedMaterials(true);
                  setUsersWithoutPostcode(false);
                  setAllAdmins(false);
                  setSuperAdminsOnly(false);
                  setShowUsersWithoutDropsInLast5Days(false);
                  setShowDroppedMaterials(false);
                  setShowUsersFromSpecificLocations(false);
                  setDroppedMaterials([]);
                  setSelectedLocations([]);
                } else {
                  setShowNonDroppedMaterials(false);
                  setNonDroppedMaterials([]);
                }
              }}
              label="Users that have not dropped the following materials:"
            />
            {showNonDroppedMaterials ? (
              <div className="w-full grid grid-cols-2 p-2 border border-gray-border rounded-xl">
                {marketPrices?.map((price) => {
                  const isChecked = nonDroppedMaterials.includes(
                    price._id as string
                  );
                  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setNonDroppedMaterials([
                        ...nonDroppedMaterials,
                        price._id as string,
                      ]);
                    } else {
                      setNonDroppedMaterials(
                        nonDroppedMaterials.filter((item) => item !== price._id)
                      );
                    }
                  };

                  return (
                    <Checkbox
                      key={price._id}
                      value={isChecked}
                      label={price.name}
                      onChange={onChange}
                    />
                  );
                })}
              </div>
            ) : null}
            <Checkbox
              value={showUsersFromSpecificLocations}
              onChange={(e) => {
                if (e.target.checked) {
                  setShowUsersFromSpecificLocations(true);
                  setUsersWithoutPostcode(false);
                  setAllAdmins(false);
                  setSuperAdminsOnly(false);
                  setShowUsersWithoutDropsInLast5Days(false);
                  setShowDroppedMaterials(false);
                  setShowNonDroppedMaterials(false);
                  setDroppedMaterials([]);
                  setNonDroppedMaterials([]);
                } else {
                  setShowUsersFromSpecificLocations(false);
                  setSelectedLocations([]);
                }
              }}
              label="Users from specific locations"
            />
            {showUsersFromSpecificLocations ? (
              <div className="w-full grid grid-cols-2 p-2 border border-gray-border rounded-xl">
                {dropLocations?.map((location) => {
                  const isChecked = selectedLocations.includes(
                    location._id as string
                  );
                  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setSelectedLocations([
                        ...selectedLocations,
                        location._id as string,
                      ]);
                    } else {
                      setSelectedLocations(
                        selectedLocations.filter((item) => item !== location._id)
                      );
                    }
                  };

                  return (
                    <Checkbox
                      key={location._id}
                      value={isChecked}
                      label={location.cost_place}
                      onChange={onChange}
                    />
                  );
                })}
              </div>
            ) : null}
            <ControlledSelect
              name="type"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.type)}
              label="Time to sent"
              options={types}
              placeholder="When do you want to send notification?"
            />
            {watch("type") === "SCHEDULED" ? (
              <ControlledDateTimePicker
                name="scheduledDate"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.scheduledDate)}
                label="Scheduled date"
                placeholder="Schedule the date to send the notification here..."
              />
            ) : null}
            <p className="w-full text-right mb-4 font-bold font-satoshi text-red-base">
              Targeted users: {usersCount}
            </p>
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Send" />
            </div>
          </form>
        </div>
        <div
          className={`w-full sm:w-1/3 sm:ml-8 bg-white border border-gray-border drop-shadow-md h-[180px]`}
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Notification preview
            </p>
          </div>
          <div className="p-5">
            <div className="w-full rounded-xl border border-gray-200 bg-white px-5 py-3 min-h-12 flex flex-row items-center justify-between">
              <div>
                <p className="font-satoshi text-sm font-medium text-tailgrids-black">
                  {watch("title") ?? "Title placeholder here..."}
                </p>
                <p className="font-satoshi text-xs text-tailgrids-black">
                  {watch("body") ?? "Body placeholder here..."}
                </p>
              </div>
              <div>
                <DroppieIcon className="w-10 h-10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddNotificationScreen);

const types = [
  { _id: "NOW", title: "Now" },
  { _id: "SCHEDULED", title: "Scheduled" },
];
